import React, { useState } from 'react';

import { Box, Grid, Typography } from '@mui/material';

import { MetricsState, ReportTypes } from '../../../propTypes';
import { reportPageStyles as styles } from '../styles';
import {
  CalculatorGraphSection,
  CalculatorInputEditModal,
  CalculatorKPICard,
  CalculatorKPIToggleModal,
} from './';
import ToggleVisibilityButton from 'src/App/Dashboards/Market/Components/Buttons/ToggleVisibilityButton';
import SubsectionHeading, { SubsectionHeadingTooltipConfig } from '../../../UtilityComponents/SubsectionHeading';

const Subsection = ({
  title,
  metrics,
  reportKPIData,
  currency,
  isUserReportOwner,
  handleEditModalOpen,
  hanldeKpiEdit,
  selectedCompsCount,
  sourceTag,
  headingTooltipConfig,
  ltmDateRange,
  showEditedBadge,
  editedMetrics,
}: {
  title: string;
  metrics: MetricsState;
  reportKPIData: any;
  currency: string;
  isUserReportOwner: boolean;
  handleEditModalOpen: () => void;
  hanldeKpiEdit: (key: string, value: any) => void;
  selectedCompsCount: number;
  sourceTag?: string;
  headingTooltipConfig?: SubsectionHeadingTooltipConfig;
  ltmDateRange: string;
  showEditedBadge?: boolean;
  editedMetrics?: string[];
}) => {
  const visibleKpis = Object.values(metrics).filter(kpi => kpi.visibility);

  if (visibleKpis.length === 0) {
    return null;
  }

  return (
    <>
      <SubsectionHeading
        title={title}
        sourceTag={sourceTag?.replace(' n ', ' ' + selectedCompsCount + ' ')}
        tooltipConfig={headingTooltipConfig}
      />
      <Grid container direction="row" alignItems="flex-start" justifyContent="flex-start" xs>
        {visibleKpis.map(kpi => (
          <CalculatorKPICard
            key={kpi.config.key}
            kpiKey={kpi.config.key}
            kpiName={kpi.config.displayName}
            value={reportKPIData[kpi.config.key]}
            valuePercentiles={{
              25: reportKPIData[25][kpi.config.key],
              50: reportKPIData[50][kpi.config.key],
              75: reportKPIData[75][kpi.config.key],
              90: reportKPIData[90][kpi.config.key],
            }}
            tooltipConfig={kpi.config.tooltipConfig}
            unitType={kpi.config.unit}
            currency={currency}
            isUserReportOwner={isUserReportOwner}
            isEditable={kpi.config.isEditable}
            handleEditModalOpen={handleEditModalOpen}
            editMode={kpi.config.editMode}
            hanldeKpiEdit={hanldeKpiEdit}
            selectedCompsCount={selectedCompsCount}
            ltmDateRange={ltmDateRange}
            showEditedBadge={showEditedBadge}
            editedMetrics={editedMetrics}
          />
        ))}
      </Grid>
    </>
  );
};

const Section = ({
  title,
  subsections,
  reportKPIData,
  currency,
  isUserReportOwner,
  metrics,
  toggleVisibility,
  trackEditOpened,
  trackEditPerformed,
  reportType,
  inputEditState,
  handleInputEdit,
  hanldeOnInputApply,
  hanldeKpiEdit,
  showFutureProjection,
  selectedCompsCount,
  handleLoginOrPricingModalOpen,
  isUserSubscribed,
  ltmDateRange,
  performanceRef,
  showEditedBadge,
  editedMetrics,
}: {
  title: string;
  subsections: {
    title: string;
    key: keyof typeof metrics;
    sourceTag?: string;
    headingTooltipConfig?: SubsectionHeadingTooltipConfig;
  }[];
  reportKPIData: any;
  currency: string;
  isUserReportOwner: boolean;
  metrics: { [key: string]: MetricsState };
  toggleVisibility: any;
  trackEditOpened: any;
  trackEditPerformed: any;
  reportType: ReportTypes;
  inputEditState: Record<string, any>;
  handleInputEdit: (key: string, value: any) => void;
  hanldeOnInputApply: () => void;
  hanldeKpiEdit: (key: string, value: any) => void;
  showFutureProjection?: boolean;
  selectedCompsCount: number;
  handleLoginOrPricingModalOpen: any;
  isUserSubscribed: boolean;
  ltmDateRange: string;
  performanceRef?: any;
  showEditedBadge?: boolean;
  editedMetrics?: string[];
}) => {
  const [toggleListOpen, setToggleListOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const groupedMetrics = subsections.map(subsection => ({
    subsection: subsection.title,
    subsectionKey: subsection.key,
    metrics: Object.entries(metrics[subsection.key]).map(([key, kpi]) => ({
      name: kpi.config.displayName,
      isVisible: kpi.visibility,
      isAllowed: kpi.allowed,
      key: key,
    })),
  }));

  if (performanceRef) {
    performanceRef.current = {
      metadata: groupedMetrics,
      data: reportKPIData,
    };
  }

  return (
    <Grid
      container
      alignItems="flex-start"
      justifyContent="center"
      xs
      sx={{ ...styles.paper, ...styles.reportKPISection }}
      gap={2}
    >
      <Grid container item xs={12} direction="row" justifyContent="space-between">
        <Typography variant="h1">{title}</Typography>
        <ToggleVisibilityButton
          title="Add or Remove Metrics"
          isDisabled={!isUserReportOwner}
          actionOnClick={() => {
            setToggleListOpen(true);
            trackEditOpened('kpi');
          }}
          isLocked={false}
        />
      </Grid>

      {subsections.map(subsection => (
        <Subsection
          key={subsection.key}
          title={subsection.title}
          metrics={metrics[subsection.key]}
          reportKPIData={reportKPIData}
          currency={currency}
          isUserReportOwner={isUserReportOwner}
          handleEditModalOpen={() => setEditModalOpen(true)}
          hanldeKpiEdit={hanldeKpiEdit}
          selectedCompsCount={selectedCompsCount}
          sourceTag={subsection.sourceTag}
          headingTooltipConfig={subsection.headingTooltipConfig}
          ltmDateRange={ltmDateRange}
          showEditedBadge={showEditedBadge}
          editedMetrics={editedMetrics}
        />
      ))}

      {showFutureProjection && (
        <Grid container item xs={12}>
          <SubsectionHeading title="Future Projection" />
          <CalculatorGraphSection
            kpiData={reportKPIData}
            reportType={reportType}
            currency={currency}
            handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
            isUserSubscribed={isUserSubscribed}
          />
        </Grid>
      )}

      <CalculatorKPIToggleModal
        isOpen={toggleListOpen}
        onClose={() => setToggleListOpen(false)}
        title={title}
        groupedMetrics={groupedMetrics}
        toggleVisibility={toggleVisibility}
      />

      <CalculatorInputEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        currency={currency}
        stateObj={inputEditState}
        handleValueChange={handleInputEdit}
        reportType={reportType}
        handleApply={hanldeOnInputApply}
      />
    </Grid>
  );
};

export default Section;
